/**
 * Change-carry modal (Ken 2026-11-02). Opened from the pane-2 "N of M changes
 * carried" chip on an executed attachment. Lists each redline change from the
 * version sent to the counterparty and whether it was carried into the signed
 * document; misses are tagged "reverted" (signed kept the old language) or
 * "modified" (clause reworded). Data comes straight off the artifact's
 * `change_carry` (list serializer) — no fetch. KEN/DEMO demo feature.
 *
 * Look & feel deliberately MIRRORS MarkupModal (Ken 2026-11-02): same backdrop,
 * same rounded surface shell, same two-line header, same sticky uppercase-mono
 * table head, same per-row colored pip (green = carried / red = missed — the
 * analog of markup's redline-red / comment-blue pip) so the two modals read as
 * one tool. Close via ✕, Esc, or a backdrop click.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import type { ChangeCarry } from '../api'

const STATUS_LABEL: Record<string, string> = {
  carried: 'carried',
  reverted: 'missed · reverted to original',
  modified: 'missed · reworded',
}

// Mirror the markup pip palette: carried green reuses the "pulled" dot green,
// misses reuse the redline red — so the two modals share one color language.
const PIP_CARRIED = '#3f9e5a'
const PIP_MISSED = '#d0453b'

export default function ChangeCarryModal({ data, filename, onClose }: {
  data: ChangeCarry
  filename: string
  onClose: () => void
}) {
  // Esc closes. Lock body scroll while open (matches MarkupModal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Changes carried for ${filename}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.55)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col rounded-lg border me-border shadow-2xl bg-[color:var(--me-surface)]"
        style={{ width: 'min(1100px, 92vw)', maxHeight: '85vh' }}
      >
        {/* Header — mirrors MarkupModal: filename on top, muted mono subline. */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b me-border shrink-0">
          <div className="min-w-0">
            <div className="me-text font-medium text-[13px] truncate" title={filename}>
              {filename || '(unnamed document)'}
            </div>
            <div className="me-text-mute text-[11px] me-mono mt-0.5">
              {data.carried} of {data.total} change{data.total === 1 ? '' : 's'} carried into Ridgemont's final
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={onClose} className="me-btn me-btn-outline" title="Close (Esc)" aria-label="Close">
              <X size={13} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="me-scroll overflow-auto">
          {data.changes.length === 0 ? (
            <div className="p-6 text-[12.5px] me-text-mute italic">No tracked changes on this document.</div>
          ) : (
            <table className="w-full table-fixed text-[12.5px]">
              <colgroup>
                <col style={{ width: '24%' }} />{/* WHERE */}
                <col style={{ width: '22%' }} />{/* STATUS — pip + carried/missed */}
                <col style={{ width: '54%' }} />{/* CHANGE */}
              </colgroup>
              <thead>
                <tr className="sticky top-0 z-10 bg-[color:var(--me-panel)] me-text-mute uppercase tracking-wider me-mono text-[10px]">
                  <th className="text-left px-4 py-2">Where</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Change</th>
                </tr>
              </thead>
              <tbody>
                {data.changes.map((c, i) => {
                  const carried = c.status === 'carried'
                  return (
                    <tr key={c.source_id || i} className="border-t me-border align-top hover:bg-[color:var(--me-panel)]">
                      <td className="px-4 py-2.5 me-text-dim me-mono" style={{ wordBreak: 'break-word' }}>
                        {c.location || <span className="me-text-mute">—</span>}
                        {c.characterization && (
                          <div className="me-text-mute text-[10.5px] mt-0.5">{c.characterization}</div>
                        )}
                      </td>
                      {/* STATUS = colored pip (green carried / red missed — the
                          analog of MarkupModal's redline/comment pip) + label. */}
                      <td className="px-3 py-2.5 me-text-dim text-[11.5px]">
                        <span className="flex items-start gap-1.5">
                          <span
                            aria-hidden
                            title={carried ? 'carried' : 'missed'}
                            style={{
                              width: 7, height: 7, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                              background: carried ? PIP_CARRIED : PIP_MISSED,
                            }}
                          />
                          <span style={{ wordBreak: 'break-word' }}>
                            {STATUS_LABEL[c.status] || c.status}
                          </span>
                        </span>
                      </td>
                      <td
                        className="px-3 py-2.5 me-text leading-relaxed"
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {c.before && (
                          <div className="me-text-mute" style={{ textDecoration: 'line-through' }}>{c.before}</div>
                        )}
                        {c.after || (!c.before && <em className="me-text-mute">(empty)</em>)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
